import React, { FC } from "react";

import Divider from "@mui/material/Divider";

import OptionalSwitch from "../../../../components/Redux/OptionalSwitch";
import Switch from "../../../../components/Redux/Switch";
import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import TabSection from "../../../../components/styled/TabSection";
import {
  getRouteFormProperty,
  setRouteFormError,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useSelector } from "../../../../store";
import { Route } from "../../../../store/pomeriumApi";
import { notEmpty } from "../../../../validators/not-empty";
import { validToUrl } from "../../../../validators/url";
import FromField from "../fields/FromField";
import PolicyPicker from "../fields/PolicyPicker";
import RouteTypeField from "../fields/RouteTypeField";
import { SshTunneling } from "./SshTunneling";
import { SectionProps } from "./common";
import useFeatureFlag from "@/hooks/use-feature-flag";
import { useIsConstrainedCluster } from "@/hooks/use-is-constrained-cluster";

type GeneralProps = SectionProps & {
  route?: Route;
};
export const General: FC<GeneralProps> = ({ readonly, tabValue, route }) => {
  const { routeMode } = useSelector((state) => state.routes);
  const isConstrainedCluster = useIsConstrainedCluster();
  const routeType = useSelector(
    getRouteFormProperty("routeType", "WEB"),
  ) as string;
  const canSshTunnel =
    useFeatureFlag("ssh-tunnels", false) &&
    !isConstrainedCluster &&
    routeType !== "UDP";

  return (
    <TabSection index={"main"} value={tabValue} title="General">
      <SettingSection label="Route Type" docsKey="route-type">
        <RouteTypeField readonly={readonly} />
      </SettingSection>
      <Divider />
      <SettingSection label="Name" docsKey="name">
        <ReduxTextField
          disabled={readonly}
          fullWidth
          path="name"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          validator={notEmpty}
        />
      </SettingSection>
      <Divider />

      <SettingSection label="Description" docsKey="description">
        <ReduxTextField
          disabled={readonly}
          fullWidth
          path="description"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>
      <Divider />
      {canSshTunnel ? (
        <>
          <SettingSection label="Reverse SSH Tunnel" docsKey="ssh-tunneling">
            <SshTunneling readonly={readonly} />
          </SettingSection>
          <Divider />
        </>
      ) : null}

      <SettingSection label="From" docsKey="from">
        <FromField readonly={readonly} />
      </SettingSection>
      <Divider />

      {routeMode === "to" && (
        <>
          <SettingSection label="To" docsKey="to">
            <ReduxTextField
              disabled={readonly}
              getter={getRouteFormProperty}
              setter={setRouteFormProperty}
              errorHandler={setRouteFormError}
              path={"to.0"}
              validator={validToUrl}
            />
          </SettingSection>
          <Divider />
        </>
      )}

      <SettingSection label="Policies" docsKey="policies">
        <PolicyPicker readonly={readonly} route={route} showAdd />
      </SettingSection>

      <Divider />

      <SettingSection
        label="Pass Identity Headers"
        docsKey="pass-identity-headers-per-route"
      >
        <OptionalSwitch
          path="passIdentityHeaders"
          label=""
          disabled={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>

      <Divider />

      <SettingSection label="Show Error Details" docsKey="show-error-details">
        <Switch
          path="showErrorDetails"
          label=""
          disabled={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>
    </TabSection>
  );
};
